import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Eye, AlertTriangle, MapPin, Phone, User, Clock, Download } from 'lucide-react';
import { useSosLogs } from 'features/sos-log/hooks/useSosLogs';
import { usePrefetchSosLogs } from 'features/sos-log/hooks/usePrefetchSosLogs';
import { formatDateTime } from 'utils/dateFormatter';
import Layout from 'components/ui/Layout';
import sosLogServices from 'features/sos-log/services/sos-log.service';

const SosLog = () => {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const { prefetchNextPage, prefetchSosLog } = usePrefetchSosLogs();

  // Get initial values from URL params
  const initialPage = parseInt(searchParams.get('page')) || 1;
  const initialLimit = parseInt(searchParams.get('limit')) || 10;
  const initialStatus = searchParams.get('status') || '';
  const initialUserType = searchParams.get('userType') || '';
  const initialAlertType = searchParams.get('alertType') || '';

  const [page, setPage] = useState(initialPage);
  const [limit, setLimit] = useState(initialLimit);
  const [status, setStatus] = useState(initialStatus);
  const [userType, setUserType] = useState(initialUserType);
  const [alertType, setAlertType] = useState(initialAlertType);
  const [sortBy, setSortBy] = useState(
  searchParams.get("sortBy") || "createdAt"
);
const [sortOrder, setSortOrder] = useState(
  searchParams.get("sortOrder") || "desc"
);

  // Create filters object
  const filters = {
  page,
  limit,
  status: status || undefined,
  userType: userType || undefined,
  alertType: alertType || undefined,
  userId: undefined,
  startDate: undefined,
  endDate: undefined,
  sortBy,
  sortOrder,
};

  // Fetch SOS logs
  const { data: response, isLoading, isError, error } = useSosLogs(filters);

  const sosLogs = response?.data?.sosLogs || [];
  const pagination = response?.data?.pagination || {};

 const handleExport = async () => {
  try {
    const response =
      await sosLogServices.exportSOSLogs(
        {
          status,
          userType,
          alertType,
          sortBy:
            filters.sortBy ||
            'createdAt',

          sortOrder:
            filters.sortOrder ||
            'desc',
        }
      );

    const blob = new Blob(
      [response.data],
      {
        type: 'text/csv',
      }
    );

    const url =
      window.URL.createObjectURL(
        blob
      );

    const link =
      document.createElement('a');

    link.href = url;

    link.download = `sos-logs-${Date.now()}.csv`;

    document.body.appendChild(
      link
    );

    link.click();

    document.body.removeChild(
      link
    );

    window.URL.revokeObjectURL(
      url
    );
  } catch (error) {
    console.error(
      'Failed to export SOS logs',
      error
    );
  }
};

  // Update URL params when filters change
useEffect(() => {
  const params = new URLSearchParams();

  params.set("page", page);
  params.set("limit", limit);

  if (status)
    params.set("status", status);

  if (userType)
    params.set("userType", userType);

  if (alertType)
    params.set("alertType", alertType);

  params.set("sortBy", sortBy);
  params.set("sortOrder", sortOrder);

  setSearchParams(params);
}, [
  page,
  limit,
  status,
  userType,
  alertType,
  sortBy,
  sortOrder,
  setSearchParams,
]);

  // Prefetch next page
  useEffect(() => {
    if (response?.data?.pagination) {
      const { currentPage, totalPages } = response.data.pagination;
      if (currentPage < totalPages) {
        prefetchNextPage(filters, totalPages);
      }
    }
  }, [response, filters]);

  const handleViewClick = (sosLog) => {
    navigate(`/sos-logs/${sosLog._id}`);
  };

  const handleFilterChange = (filterType, value) => {
    setPage(1); // Reset to page 1 when filters change
    switch (filterType) {
      case 'status':
        setStatus(value);
        break;
      case 'userType':
        setUserType(value);
        break;
      case 'alertType':
        setAlertType(value);
        break;
      default:
        break;
    }
  };

  const handlePageChange = (newPage) => {
    setPage(newPage);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSort = (column) => {
  const newOrder =
    sortBy === column && sortOrder === "asc"
      ? "desc"
      : "asc";

  setSortBy(column);
  setSortOrder(newOrder);
  setPage(1);
};

const SortIcon = ({ column }) => {
  if (sortBy !== column) {
    return (
      <span className="text-gray-400">
        ↕
      </span>
    );
  }

  return sortOrder === "asc"
    ? <span>↑</span>
    : <span>↓</span>;
};

  const getStatusColor = (status) => {
    const colors = {
      triggered: 'bg-red-500/10 text-red-600 dark:text-red-400',
      acknowledged: 'bg-yellow-500/10 text-yellow-600 dark:text-yellow-400',
      resolved: 'bg-green-500/10 text-green-600 dark:text-green-400',
      cancelled: 'bg-gray-500/10 text-gray-600 dark:text-gray-400',
    };
    return colors[status] || 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
  };

  const getUserTypeColor = (type) => {
    const colors = {
      customer: 'bg-blue-500/10 text-blue-600 dark:text-blue-400',
      seller: 'bg-purple-500/10 text-purple-600 dark:text-purple-400',
    };
    return colors[type] || 'bg-gray-500/10 text-gray-600 dark:text-gray-400';
  };

  if (isError) {
    return (
      <Layout>
        <div className='flex flex-col items-center justify-center h-screen'>
          <div className='text-destructive text-lg mb-4'>Error loading SOS logs</div>
          <p className='text-muted-foreground'>{error?.message || 'Something went wrong'}</p>
        </div>
      </Layout>
    );
  }

  

  return (
    <Layout>
      <div className='p-6 space-y-6'>
        {/* Header */}
        <div className='flex items-center justify-between'>
          <div>
            <h1 className='text-3xl font-bold flex items-center gap-2'>
              <AlertTriangle className='text-red-500' size={32} />
              SOS Logs
            </h1>
            <p className='text-muted-foreground mt-1'>Emergency SOS alerts and logs management</p>
          </div>

          <div className='flex items-center gap-3'>
  {/* {!showPermissionError && ( */}
  <button
    onClick={handleExport}
    className='flex items-center space-x-2 px-4 py-2 text-sm border border-border rounded-md bg-card text-card-foreground hover:bg-accent transition-colors'
  >
    <Download size={16} />
    <span>Export</span>
  </button>
{/* )} */}
</div>
        </div>

        {/* Filters */}
        <div className='bg-card border border-border rounded-lg p-4'>
          <div className='grid grid-cols-1 md:grid-cols-4 gap-4'>
            {/* Status Filter */}
            <div>
              <label className='block text-sm font-medium mb-2'>Status</label>
              <select
                value={status}
                onChange={(e) => handleFilterChange('status', e.target.value)}
                className='w-full px-3 py-2 text-sm border border-border rounded-md bg-background'
              >
                <option value=''>All Statuses</option>
                <option value='triggered'>Triggered</option>
                <option value='acknowledged'>Acknowledged</option>
                <option value='resolved'>Resolved</option>
                <option value='cancelled'>Cancelled</option>
              </select>
            </div>

            {/* User Type Filter */}
            <div>
              <label className='block text-sm font-medium mb-2'>User Type</label>
              <select
                value={userType}
                onChange={(e) => handleFilterChange('userType', e.target.value)}
                className='w-full px-3 py-2 text-sm border border-border rounded-md bg-background'
              >
                <option value=''>All Types</option>
                <option value='customer'>Customer</option>
                <option value='seller'>Seller</option>
              </select>
            </div>

            {/* Alert Type Filter */}
            <div>
              <label className='block text-sm font-medium mb-2'>Alert Type</label>
              <select
                value={alertType}
                onChange={(e) => handleFilterChange('alertType', e.target.value)}
                className='w-full px-3 py-2 text-sm border border-border rounded-md bg-background'
              >
                <option value=''>All Alert Types</option>
                <option value='sms'>SMS</option>
                <option value='phone_call'>Phone Call</option>
                <option value='whatsapp'>WhatsApp</option>
                <option value='in_app_push'>In-App Push</option>
              </select>
            </div>

            {/* Items Per Page */}
            <div>
              <label className='block text-sm font-medium mb-2'>Items Per Page</label>
              <select
                value={limit}
                onChange={(e) => {
                  setLimit(parseInt(e.target.value));
                  setPage(1);
                }}
                className='w-full px-3 py-2 text-sm border border-border rounded-md bg-background'
              >
                <option value='5'>5</option>
                <option value='10'>10</option>
                <option value='20'>20</option>
                <option value='50'>50</option>
              </select>
            </div>
          </div>
        </div>

        {/* Table */}
        <div className='bg-card border border-border rounded-lg overflow-hidden'>
          <div className='overflow-x-auto'>
            <table className='w-full'>
              <thead className='bg-muted border-b border-border'>
                <tr>
                  <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>
                    User
                  </th>
                  {/* <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>
                    User Type
                  </th> */}
                  <th
  className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider hover:text-foreground"
  // onClick={() => handleSort("userType")}
>
  <div className="flex items-center space-x-1">
    <span>User Type</span>
    {/* <SortIcon column="userType" /> */}
  </div>
</th>
                  <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>
                    Emergency Contact
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>
                    Location
                  </th>
                  {/* <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>
                    Alert Type
                  </th> */}
                  <th
  className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
  onClick={() => handleSort("alertType")}
>
  <div className="flex items-center space-x-1">
    <span>Alert Type</span>
    <SortIcon column="alertType" />
  </div>
</th>
                  {/* <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>
                    Status
                  </th> */}

                  <th
  className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
  onClick={() => handleSort("status")}
>
  <div className="flex items-center space-x-1">
    <span>Status</span>
    <SortIcon column="status" />
  </div>
</th>
                  {/* <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>
                    Triggered At
                  </th> */}

                  <th
  className="px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider cursor-pointer hover:text-foreground"
  onClick={() => handleSort("createdAt")}
>
  <div className="flex items-center space-x-1">
    <span>Triggered At</span>
    <SortIcon column="createdAt" />
  </div>
</th>
                  <th className='px-4 py-3 text-left text-xs font-medium text-muted-foreground uppercase tracking-wider'>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className='divide-y divide-border'>
                {isLoading ? (
                  <tr>
                    <td colSpan='8' className='px-4 py-8 text-center'>
                      <div className='flex items-center justify-center'>
                        <div className='animate-spin rounded-full h-8 w-8 border-b-2 border-primary'></div>
                      </div>
                    </td>
                  </tr>
                ) : sosLogs.length === 0 ? (
                  <tr>
                    <td colSpan='8' className='px-4 py-8 text-center text-muted-foreground'>
                      No SOS logs found
                    </td>
                  </tr>
                ) : (
                  sosLogs.map((log) => (
                    <tr key={log._id} className='hover:bg-muted/50 transition-colors'>
                      <td className='px-4 py-3'>
                        <div className='flex items-center space-x-3'>
                          {log.user?.profilePhoto ? (
                            <img
                              src={log.user.profilePhoto}
                              alt={`${log.user.firstName} ${log.user.lastName}`}
                              className='w-8 h-8 rounded-full object-cover'
                            />
                          ) : (
                            <div className='w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center'>
                              <User size={16} />
                            </div>
                          )}
                          <div>
                            <p className='text-sm font-medium whitespace-nowrap'>
                              {log.user?.firstName} {log.user?.lastName}
                            </p>
                            <p className='text-xs text-muted-foreground whitespace-nowrap'>
                              {log.user?.countryCode} {log.user?.phoneNumber}
                            </p>
                          </div>
                        </div>
                      </td>
                      <td className='px-4 py-3'>
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getUserTypeColor(
                            log.userType
                          )}`}
                        >
                          {log.userType?.toUpperCase()}
                        </span>
                      </td>
                      <td className='px-4 py-3'>
  {log.emergencyContacts?.length ? (
    <div className='text-sm'>
      <p className='font-medium'>
        {log.emergencyContacts[0]?.name}
      </p>

      <p className='text-xs text-muted-foreground flex items-center gap-1 whitespace-nowrap'>
        <Phone size={10} />
        {log.emergencyContacts[0]?.countryCode}{' '}
        {log.emergencyContacts[0]?.phoneNumber}
      </p>

      <p className='text-xs text-muted-foreground'>
        {log.emergencyContacts[0]?.relationship}
      </p>

                            {log.emergencyContacts.length > 1 && (
                              // <p className='text-xs text-primary mt-1'>
                              // +{log.emergencyContacts.length - 1} more
                              // </p>

                              <button
                                className='text-xs text-primary mt-1 hover:text-primary/80'
                                onClick={() => handleViewClick(log)}>

                                +{log.emergencyContacts.length - 1} more

                              </button>
                            )}
    </div>
  ) : (
    <span className='text-xs text-muted-foreground'>
      No Contacts
    </span>
  )}
</td>
                      <td className='px-4 py-3'>
                        <div className='flex items-center gap-1 text-sm text-muted-foreground'>
                          <MapPin size={14} />
                          <span>
                            {log.location?.coordinates?.[1]?.toFixed(4)}, {log.location?.coordinates?.[0]?.toFixed(4)}
                          </span>
                        </div>
                      </td>
                      <td className='px-4 py-3'>
                        <span className='text-sm'>{log.alertType?.toUpperCase()}</span>
                      </td>
                      <td className='px-4 py-3'>
                        <span
                          className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${getStatusColor(
                            log.status
                          )}`}
                        >
                          {log.status?.replace(/_/g, ' ')?.toUpperCase()}
                        </span>
                      </td>
                      <td className='px-4 py-3'>
                        <div className='flex items-center gap-1 text-sm text-muted-foreground'>
                          <Clock size={14} />
                          <span>{formatDateTime(log.createdAt)}</span>
                        </div>
                      </td>
                      <td className='px-4 py-3'>
                        <button
                          onClick={() => handleViewClick(log)}
                          onMouseEnter={() => prefetchSosLog(log._id)}
                          className='inline-flex items-center px-3 py-1 text-sm bg-primary/10 text-primary rounded-md hover:bg-primary/20 transition-colors'
                        >
                          <Eye size={14} className='mr-1' /> 
                          View                         
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {pagination.totalPages > 1 && (
            <div className='px-4 py-3 border-t border-border flex items-center justify-between'>
              <div className='text-sm text-muted-foreground'>
                Showing {(pagination.currentPage - 1) * pagination.itemsPerPage + 1} to{' '}
                {Math.min(pagination.currentPage * pagination.itemsPerPage, pagination.totalItems)} of{' '}
                {pagination.totalItems} results
              </div>
              <div className='flex items-center space-x-2'>
                <button
                  onClick={() => handlePageChange(page - 1)}
                  disabled={page === 1}
                  className='px-3 py-1 text-sm border border-border rounded-md hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                >
                  Previous
                </button>
                <span className='text-sm text-muted-foreground'>
                  Page {pagination.currentPage} of {pagination.totalPages}
                </span>
                <button
                  onClick={() => handlePageChange(page + 1)}
                  disabled={page === pagination.totalPages}
                  className='px-3 py-1 text-sm border border-border rounded-md hover:bg-accent disabled:opacity-50 disabled:cursor-not-allowed transition-colors'
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
};

export default SosLog;
